/**
 * Two tiers of usage measurement (owner, 2026-10-08: "Turn on monitoring usage anonymously ... you can [ask] in a way that is fun").
 *
 *  ANONYMOUS, on by default, no ask: aggregate counts with nothing stored on or read from the device: no cookie, no localStorage
 *  id, no fingerprint, no account, no IP kept. An event is a page or screen, a tapped control, time on screen, and a coarse device
 *  and size class, nothing else. `anonymousEvent` is the only way to make one: it builds a fresh object from those fields, so a user,
 *  business, email, IP or health detail cannot ride along. Health screens are never measured (FRAMEWORK T4). The server only counts.
 *
 *  LINKED, opt-in (the usage_data consent, off until a yes; the cookie banner's Analytics, or the fun ask): paths across visits,
 *  the funnel by account, heatmaps by session. `measureRouter` sends linked events only while that consent holds, anonymous otherwise.
 */

import { NEVER_FIRST } from "../siteanalytics.ts";
import { siteControlShape } from "../sitevisits.ts";

export type AnonKind = "view" | "tap" | "time";
export type DeviceClass = "phone" | "tablet" | "desktop";
export type SizeClass = "s" | "m" | "l";
export type Surface = "site" | "web" | "phone";

export interface AnonymousEvent {
  surface: Surface;
  kind: AnonKind;
  /** The page or screen, with ids taken out ("/orgs/:id/today"). */
  path: string;
  /** A developer-written control name ("save-reading"), never text a person typed. */
  control?: string;
  /** Time on screen, whole seconds, capped at 30 minutes. */
  seconds?: number;
  device: DeviceClass;
  size: SizeClass;
}
export const ANON_FIELDS = ["surface", "kind", "path", "control", "seconds", "device", "size"] as const;

/** Paths about health never produce an event (T4): check-ins, sick reports, illness, symptoms. */
export const HEALTH_PATH = /(^|\/)(check-?in|checkins?|health|sick|ill(ness)?|symptoms?|exclusion|return-to-work|vomit|diarrh)/i;
const ID_SEGMENT = /^(\d+|[0-9a-f]{8,}|[0-9a-f-]{20,}|[0-9A-HJKMNP-TV-Z]{20,}|.*@.*|[A-Za-z0-9_-]{24,})$/;
const CONTROL = /^[a-z][a-z0-9._:-]{0,59}$/;

/** Removes the query, the hash and every id-like segment; null for a health path or anything that is not a plain path. */
export function anonymousPath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const p = raw.split(/[?#]/)[0]!.slice(0, 200);
  if (!p.startsWith("/") || HEALTH_PATH.test(p)) return null;
  const parts = p.split("/").map((s, i) => (i === 0 ? s : ID_SEGMENT.test(s) ? ":id" : s.toLowerCase()));
  const out = parts.join("/");
  return /^[a-z0-9/:_.-]*$/.test(out) ? out : null;
}

export const deviceClass = (width: number, coarse: boolean): DeviceClass => (width < 600 ? "phone" : coarse || width < 1024 ? "tablet" : "desktop");
export const sizeClass = (width: number): SizeClass => (width < 600 ? "s" : width < 1200 ? "m" : "l");

/**
 * A page of the public site, as sent: a plain directory path ("/blog/how-long-to-keep-logs/"), never a query, never an app screen
 * (core NEVER_FIRST). Site pages are public articles, so their slugs stay whole (a long slug is not an id, and the server counts a
 * site path only when the landing build has that page); the health-word rule below is for the apps' screens, where a person's
 * answers live (T4). Null otherwise.
 */
export function anonymousSitePath(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length > 200) return null;
  const p = raw.split(/[?#]/)[0]!.replace(/index\.html$/, "");
  if (!/^\/(?:[a-z0-9][a-z0-9-]{0,79}\/){0,6}$/.test(p)) return null;
  return NEVER_FIRST.has(p.split("/")[1] ?? "") ? null : p;
}

/** The one way to make an anonymous event. Anything not listed is dropped; a health path or a bad value gives null (nothing sent). */
export function anonymousEvent(input: Record<string, unknown>): AnonymousEvent | null {
  const surface = input.surface, kind = input.kind;
  if (surface !== "site" && surface !== "web" && surface !== "phone") return null;
  if (kind !== "view" && kind !== "tap" && kind !== "time") return null;
  const path = surface === "site" ? anonymousSitePath(input.path) : anonymousPath(input.path);
  if (!path) return null;
  const width = typeof input.width === "number" && Number.isFinite(input.width) ? input.width : 1024;
  const ev: AnonymousEvent = { surface, kind, path, device: deviceClass(width, input.coarse === true), size: sizeClass(width) };
  if (kind === "tap") {
    // The site names a click by what it is (core sitevisits.ts: cta:, to:, out:, cc:, id:); the apps by a developer-written name.
    const control = surface === "site" ? siteControlShape(input.control) : typeof input.control === "string" && CONTROL.test(input.control) ? input.control : null;
    if (!control) return null;
    ev.control = control;
  }
  if (kind === "time") { const s = Number(input.seconds); if (!Number.isFinite(s) || s < 0) return null; ev.seconds = Math.min(1800, Math.round(s)); }
  return ev;
}

/** True only for an object made of the anonymous fields with no value that looks like a person, an address or an id. */
export function isAnonymous(ev: unknown): boolean {
  if (!ev || typeof ev !== "object") return false;
  for (const [k, v] of Object.entries(ev)) {
    if (!(ANON_FIELDS as readonly string[]).includes(k)) return false;
    if (typeof v === "string" && (/@/.test(v) || /\b\d{1,3}(\.\d{1,3}){3}\b/.test(v) || /[0-9a-f]{16,}/i.test(v))) return false;
  }
  return true;
}

/**
 * The one line every sender wires: `const measure = measureRouter({ linkedAllowed, anonymous, linked })`. A linked sender is called
 * only while `linkedAllowed()` is true (consent); otherwise the event goes the anonymous way, made by anonymousEvent (or dropped).
 */
export function measureRouter<L>(o: { linkedAllowed: () => boolean; anonymous: (e: AnonymousEvent) => void; linked?: (e: AnonymousEvent, extra: L) => void }) {
  return (input: Record<string, unknown>, extra?: L): "linked" | "anonymous" | "dropped" => {
    const ev = anonymousEvent(input);
    if (!ev) return "dropped";
    if (o.linked && extra !== undefined && o.linkedAllowed()) { o.linked(ev, extra); return "linked"; }
    o.anonymous(ev);
    return "anonymous";
  };
}

/** The fun ask for LINKED measurement: one card, once, after the first finished check; the same words on the web and the phone. */
export const USAGE_ASK = {
  id: "usage-ask-2026-10-08",
  moment: "check_logged",
  title: "Help us make CleanCleaver better?",
  body: "We'll see which buttons you tap (never what you write or your health answers).",
  yes: "Sure, count me in",
  no: "No thanks",
  after: "Change it any time: Profile, then Change cookie choices.",
} as const;
